import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { useLang } from "../i18n";

export const Footer = () => {
  const { l, data, t } = useLang();
  const { SITE, NAV_LINKS, ROOMS } = data;

  return (
    <footer data-testid="site-footer" className="bg-sage-dark text-cream/90 mt-0">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <img src="/logo-full-light.png" alt="Le dolci Dimore — Mantova" className="h-24 w-auto" />
          <p className="mt-5 text-sm leading-relaxed text-cream/70 max-w-xs">
            {t.footer.description}
          </p>
        </div>
        <nav className="md:col-span-2" aria-label={t.footer.explore}>
          <p className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-5">{t.footer.explore}</p>
          <ul className="space-y-3">
            {NAV_LINKS.map((item) => (
              <li key={item.to}>
                <Link
                  to={l(item.to)}
                  data-testid={`footer-link-${item.to.slice(1)}`}
                  className="text-sm text-cream/80 hover:text-cream transition-colors duration-300"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <nav className="md:col-span-3" aria-label={t.footer.rooms}>
          <p className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-5">{t.footer.rooms}</p>
          <ul className="space-y-3">
            {ROOMS.map((r) => (
              <li key={r.slug}>
                <Link
                  to={l(`/camere/${r.slug}`)}
                  data-testid={`footer-room-link-${r.slug}`}
                  className="text-sm text-cream/80 hover:text-cream transition-colors duration-300"
                >
                  {r.name} · {t.footer.maxGuests} {r.capacity}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="md:col-span-3">
          <p className="text-xs uppercase tracking-[0.2em] text-cream/50 mb-5">{t.footer.contacts}</p>
          <ul className="space-y-3 text-sm text-cream/80">
            <li className="flex gap-2.5">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-cream/50" />
              <span>
                {SITE.address}, {SITE.cap} {SITE.city}
              </span>
            </li>
            <li>
              <a
                href={SITE.phoneHref}
                data-testid="footer-phone-link"
                className="flex gap-2.5 hover:text-cream transition-colors duration-300"
              >
                <Phone className="w-4 h-4 mt-0.5 shrink-0 text-cream/50" />
                {SITE.phone}
              </a>
            </li>
            <li>
              <a
                href={SITE.emailHref}
                data-testid="footer-email-link"
                className="flex gap-2.5 hover:text-cream transition-colors duration-300"
              >
                <Mail className="w-4 h-4 mt-0.5 shrink-0 text-cream/50" />
                {SITE.email}
              </a>
            </li>
          </ul>
          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-booking-button"
            className="mt-6 inline-flex items-center rounded-full bg-terra px-6 py-3 text-[13px] font-semibold text-cream transition-[background-color,transform] duration-300 hover:bg-terra-dark hover:-translate-y-0.5"
          >
            {t.footer.booking}
          </a>
        </div>
      </div>
      <div className="border-t border-cream/15">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-cream/50">
          <p>© {new Date().getFullYear()} Le dolci Dimore · {t.footer.rights}: {SITE.legalName}</p>
          <div className="flex gap-5">
            <Link to={l("/privacy-policy")} data-testid="footer-privacy-link" className="hover:text-cream transition-colors duration-300">
              {t.footer.privacy}
            </Link>
            <Link to={l("/cookie-policy")} data-testid="footer-cookie-link" className="hover:text-cream transition-colors duration-300">
              {t.footer.cookie}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
